import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import type { ElementType, FocusEvent, InputEvent, KeyboardEvent } from 'react'
import { cn } from './cn'

/** Mirrors the server filename cap (`normalizeFilename`, code-point counted). */
export const INLINE_EDITABLE_MAX_CHARS = 255

/** Flatten contentEditable whitespace (incl. pasted newlines) to a single trimmed line. */
export function normalizeEditableText(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

/**
 * The one shape for inline view & edit. Rest state is plain text — no border, no
 * fill; hover and focus are a soft fill and nothing else.
 *
 * There is deliberately NO focus outline. A `[contenteditable]` element matches
 * `:focus-visible` on a plain mouse click (the selector matches whenever the
 * element expects text entry, not only on keyboard traversal), so a
 * `:focus-visible` ring is not the "keyboard only" affordance it looks like in
 * source — it fires on every click and reads as a form control, which is the
 * one thing in-place editing exists to avoid. The soft fill plus the caret is
 * the focus indicator.
 *
 * The fill is sized on the INLINE axis only — `px-1.5` with an equal `-mx-1.5`,
 * so the text's left edge does not move between rest and hover. The block axis
 * is left completely alone, for two reasons:
 *
 * 1. A margin shorthand (or a block-axis margin longhand) silently overwrites
 *    whatever spacing the page gave this element. `.dic-sub { margin-top: 4px }`
 *    under a heading was clobbered exactly this way; the same class ships to
 *    every consumer, so the failure is one page-supplied margin away anywhere.
 * 2. A negative block margin escapes: if the owner wraps title + description in
 *    a plain `<div>` (no padding, no border), the negative margins collapse out
 *    through it, so the fill paints past the wrapper's own box edge and every
 *    gap measured from that edge is off by the same amount.
 *
 * Vertical breathing room is already in the line box (13px text in a 21.45px
 * line box is 4.2px above and below the glyphs), so the fill needs no padding
 * to look right — verified rendered, not reasoned about.
 */
const INLINE_EDIT_BASE =
  '-mx-1.5 cursor-text rounded-[6px] px-1.5 outline-none transition-colors ' +
  'hover:bg-[var(--fg-soft)] focus:bg-[var(--fg-soft)] ' +
  'aria-[busy=true]:cursor-default aria-[busy=true]:opacity-60 ' +
  'empty:before:text-[var(--muted)] empty:before:[content:attr(data-placeholder)]'

export interface InlineEditableOptions {
  /** Called after editing ends (commit, cancel, or no-op) — toggle an external editing flag. */
  onClose?: () => void
  maxLength?: number
}

export interface InlineEditable<T extends HTMLElement> {
  /** Ref callback for the editable element — seeds its text the moment it attaches. */
  ref: (el: T | null) => void
  elementProps: {
    contentEditable: boolean
    suppressContentEditableWarning: true
    spellCheck: false
    onFocus: (e: FocusEvent<T>) => void
    onKeyDown: (e: KeyboardEvent<T>) => void
    onInput: (e: InputEvent<T>) => void
    onBlur: (e: FocusEvent<T>) => void
  }
  /** Focus the element + drop the caret at the end (don't select-all). */
  focusEnd: () => void
  busy: boolean
}

/**
 * Edit text IN PLACE via contentEditable (no input box). Prefer the `InlineEdit`
 * component below; reach for the hook directly only when the owner must supply
 * an element the component can't express.
 *
 * - Focus the element (click it, or call `focusEnd` from a pencil) to edit;
 *   Enter or blur commits, Escape cancels.
 * - `onCommit(next)` persists and may throw → the text reverts and `onError`
 *   fires (e.g. a toast). The field is read-only (`busy`) until it settles, so a
 *   refocus-and-retype can't silently drop an edit.
 * - A value unchanged after whitespace normalization is a no-op; an overlong
 *   value is capped to `maxLength` code points before committing.
 *
 * The `ref` is a stable callback that seeds the element's text on attach, so it
 * works for a conditionally-mounted editor (e.g. a list row revealed by a pencil)
 * as well as an always-mounted one (the detail heading).
 */
export function useInlineEditable<T extends HTMLElement>(
  value: string,
  onCommit: (next: string) => Promise<void> | void,
  onError?: (cause: unknown) => void,
  options?: InlineEditableOptions & {
    /** Commit an emptied value instead of silently reverting — for optional
     *  fields (the ones that show a placeholder). Required fields keep the
     *  default: clearing them is an unfinished gesture, not a request. */
    allowEmpty?: boolean
  }
): InlineEditable<T> {
  const elRef = useRef<T | null>(null)
  const snapshot = useRef<string | null>(null)
  const valueRef = useRef(value)
  valueRef.current = value
  const [busy, setBusy] = useState(false)
  const max = options?.maxLength ?? INLINE_EDITABLE_MAX_CHARS

  const ref = useCallback((el: T | null) => {
    elRef.current = el
    if (el && document.activeElement !== el) el.textContent = valueRef.current
  }, [])

  // Re-sync on later value changes (e.g. a server-normalized rename) when the
  // element isn't focused, so an external update doesn't clobber in-progress typing.
  useEffect(() => {
    const el = elRef.current
    if (el && document.activeElement !== el) el.textContent = value
  }, [value])

  // Stable identity: the imperative handle is rebuilt from this, and a consumer
  // holding it in state must not see a new object every render.
  const busyRef = useRef(busy)
  busyRef.current = busy
  const focusEnd = useCallback(() => {
    const el = elRef.current
    if (!el || busyRef.current) return
    el.focus()
    const range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(false)
    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)
  }, [])

  const close = () => options?.onClose?.()

  const commit = async (el: T) => {
    const raw = normalizeEditableText(el.textContent ?? '')
    const next = [...raw].length > max ? [...raw].slice(0, max).join('') : raw
    if ((!next && !options?.allowEmpty) || next === normalizeEditableText(value)) {
      el.textContent = value
      close()
      return
    }
    if (busy) return
    setBusy(true)
    try {
      await onCommit(next)
    } catch (cause) {
      el.textContent = value
      // No silent catch: a caller that omits onError still gets the failure on
      // the console next to the reverted text.
      if (onError) onError(cause)
      else console.error('[inline-edit] commit failed — reverted to previous value', cause)
    } finally {
      setBusy(false)
      close()
    }
  }

  return {
    ref,
    elementProps: {
      contentEditable: !busy,
      suppressContentEditableWarning: true,
      spellCheck: false,
      onFocus: (e) => {
        snapshot.current = e.currentTarget.textContent
      },
      onKeyDown: (e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          e.currentTarget.blur()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          e.currentTarget.textContent = value
          e.currentTarget.blur()
        }
      },
      onInput: (e) => {
        // Select-all + Delete leaves a lone <br> child, which keeps `:empty`
        // from matching and hides the placeholder. Text content is untouched.
        const el = e.currentTarget
        if (!el.textContent && el.firstChild) el.replaceChildren()
      },
      onBlur: (e) => {
        if (e.currentTarget.textContent === snapshot.current) {
          e.currentTarget.textContent = value
          close()
          return
        }
        void commit(e.currentTarget)
      },
    },
    focusEnd,
    busy,
  }
}

export interface InlineEditHandle {
  /** Drop the caret at the end — what a pencil affordance calls. */
  focusEnd: () => void
}

export interface InlineEditProps {
  value: string
  onCommit: (next: string) => Promise<void> | void
  onError?: (cause: unknown) => void
  onClose?: () => void
  maxLength?: number
  /** Shown in place when the value is empty. Omit for required fields. */
  placeholder?: string
  /** Element to render — use the tag the text would have used if it weren't editable. */
  as?: ElementType
  /** Composition only (type scale, grid placement). Do not restate the base visual. */
  className?: string
  'aria-label': string
}

/**
 * One element that is both the reading state and the writing state. Clicking the
 * text puts a caret in it; Enter or blur commits, Escape reverts. There is no
 * view mode, no edit mode, and no Save button.
 *
 * Render it as the tag the text would have had anyway — the type scale belongs
 * to the page, the editing affordance belongs here:
 *
 *   <InlineEdit as="h1" className="text-xl font-semibold" aria-label="標題"
 *               value={name} onCommit={rename} />
 *
 * `className` takes Tailwind utilities for composition. Passing a legacy page
 * class (e.g. `.doc-title`) is a trap: unlayered page CSS outranks Tailwind's
 * layered utilities, so its own geometry silently overrides the base visual.
 *
 * For a pencil affordance, hold a ref and call `focusEnd()`; a pencil is a
 * shortcut into this same element, never a different editor.
 */
export const InlineEdit = forwardRef<InlineEditHandle, InlineEditProps>(function InlineEdit(
  { value, onCommit, onError, onClose, maxLength, placeholder, as, className, ...rest },
  ref
) {
  const editable = useInlineEditable<HTMLElement>(value, onCommit, onError, {
    onClose,
    maxLength,
    // A placeholder marks the field optional (§ props doc) — optional fields
    // may be cleared; required ones silently revert on empty.
    allowEmpty: placeholder !== undefined,
  })
  useImperativeHandle(ref, () => ({ focusEnd: editable.focusEnd }), [editable.focusEnd])

  const Tag = (as ?? 'div') as ElementType
  // A semantic tag keeps its native role — stamping `textbox` on an `as="h2"`
  // would strip the page's heading from the accessibility tree. The generic
  // wrappers get `textbox` so the editable strip is still announced as a field.
  const role = as === undefined || as === 'div' || as === 'span' ? 'textbox' : undefined

  return (
    <Tag
      ref={editable.ref}
      role={role}
      data-placeholder={placeholder}
      aria-busy={editable.busy || undefined}
      className={cn(INLINE_EDIT_BASE, className)}
      {...editable.elementProps}
      {...rest}
    />
  )
})
